import { useIntl } from 'react-intl';
import { Row, Col, InputGroup } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Field, Formik, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { ethers } from 'ethers';
import Select from 'react-select';

import { CardBody, Input } from '../../../../_metronic/_partials/controls';
import * as actions from '../../../../store/actions/index';
import { useReadContractHelper } from '../../../../helpers/useReadContractHelper';
import { tokenizationAbi } from '../../../../config/abi';
import { ether } from '../../../../helpers/ethers';

import './CryptoRevenueForm.scss';
import { useAppKit } from '@reown/appkit/react';
import { connectionContext } from '../../../connect-wallet/ConnectionStatus';
import useContractInteraction from '../../../../helpers/useContractInteraction';

export const TokenizationRevenueForm = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { open } = useAppKit();
  const { address, isConnected, isReconnecting, isConnecting } =
    useContext(connectionContext);

  // normalize payments from redux slice to array
  const { payments: rawPayments } = useSelector(
    (state) => state.paymentGateway,
  );
  const payments = Array.isArray(rawPayments) ? rawPayments : [];

  const { taxonomy } = useSelector((state) => state?.taxonomy);
  const { currentPermissions } = useSelector((state) => state.role);
  const blockchainOption = payments.filter(Boolean).map((option) => ({
    value: option.chainId,
    label: option.title,
  }));

  const [selectedChainId, setSelectedChainId] = useState(
    blockchainOption.length > 0
      ? blockchainOption[0].value
      : import.meta.env.VITE_DEFAULT_CHAINID,
  );

  const {
    data: getPlatformDetailsData,
    interact: getPlatformDetails,
    refetch: refetchPlatformDetails,
  } = useReadContractHelper();
  const {
    interact,
    isLoading: isInteracting,
    isSuccess,
  } = useContractInteraction();

  const percentage = Yup.number()
    .positive()
    .label(intl.formatMessage({ id: 'PERCENTAGE.ERROR.MESSAGE' }))
    .max(100)
    .required();

  const fixed = Yup.number()
    .positive()
    .required()
    .label(intl.formatMessage({ id: 'FIXED.ERROR.MESSAGE' }));

  const validationSchema = Yup.object().shape({
    investType: Yup.string(),
    percentageCampaignCommission: percentage,
    fixedCampaignCommission: fixed,
    percentageGatewayFees: percentage,
    fixedGatewayFees: fixed,
  });

  const contractDetails = useMemo(() => {
    if (payments && selectedChainId) {
      const currChainDetails = payments.find(
        (payment) => Number(payment.chainId) === Number(selectedChainId),
      );
      return {
        address: currChainDetails?.tokenizationContractAddress,
        chainId: currChainDetails?.chainId,
        currencySymbol: currChainDetails?.currencyCode,
      };
    } else {
      return {
        address: '',
        chainId: '',
        currencySymbol: 'ETH',
      };
    }
  }, [payments, selectedChainId]);

  const initialValues = useMemo(() => {
    if (getPlatformDetailsData && blockchainOption) {
      return {
        percentageCampaignCommission: ether.formatContractPercentageFrom(
          getPlatformDetailsData[0],
        ),
        fixedCampaignCommission: ethers.formatEther(getPlatformDetailsData[2]),
        percentageGatewayFees: ether.formatContractPercentageFrom(
          getPlatformDetailsData[1],
        ),
        fixedGatewayFees: ethers.formatEther(getPlatformDetailsData[3]),
      };
    } else {
      return {
        percentageCampaignCommission: '0.00',
        fixedCampaignCommission: '0.00',
        percentageGatewayFees: '0.00',
        fixedGatewayFees: '0.00',
      };
    }
  }, [getPlatformDetailsData, blockchainOption]);

  const handleSubmitForm = async (values) => {
    if (!isConnected) {
      return open();
    } else {
      await interact(
        ethers.getAddress(contractDetails.address),
        tokenizationAbi,
        'updatePlateformFees',
        [
          ether.formatContractPercentageTo(values.percentageCampaignCommission),
          ether.parseEther(values.fixedCampaignCommission),
          ether.formatContractPercentageTo(values.percentageGatewayFees),
          ether.parseEther(values.fixedGatewayFees),
        ],
        values.chainId,
        ethers.getAddress(address),
      );
    }
  };

  useEffect(() => {
    if (contractDetails.address && contractDetails.chainId) {
      getPlatformDetails(
        tokenizationAbi,
        contractDetails.address,
        'getPlatformDetails',
        [],
        contractDetails.chainId,
      );
    }
  }, [contractDetails]);

  useEffect(() => {
    dispatch(actions.getPaymentGateway('crypto-payment'));
  }, [dispatch]);

  useEffect(() => {
    if (isSuccess) {
      refetchPlatformDetails();
    }
  }, [isSuccess]);

  return (
    <CardBody className='px-4 pt-0 py-6'>
      <Row className='justify-content-md-center'>
        <Col md={11}>
          <Formik
            enableReinitialize={true}
            initialValues={{ ...initialValues, chainId: selectedChainId }}
            validationSchema={validationSchema}
          >
            {({ values, setFieldValue }) => {
              return (
                <Form className='form form-label=right'>
                  <div className='form-group floating-input-field-wrapper mb-5'>
                    <label
                      htmlFor='chainId'
                      className='floating-input-field-label-static ms-4 start-0'
                    >
                      {intl.formatMessage({
                        id: 'BLOCKCHAIN.SELECT',
                      })}
                    </label>
                    <Select
                      name='chainId'
                      options={blockchainOption}
                      classNamePrefix='react-select'
                      value={blockchainOption.find(
                        (option) => option.value === Number(values.chainId),
                      )} // Formik value
                      onChange={(selectedOption) => {
                        setFieldValue('chainId', selectedOption?.value); // update Formik
                        setSelectedChainId(selectedOption?.value); // update local state
                      }}
                      placeholder={intl.formatMessage({
                        id: 'BLOCKCHAIN.SELECT',
                      })}
                    />
                    <Field type='hidden' name='chainId' />
                  </div>

                  <div className='form-group'>
                    <label className='text-black font-size-sm'>
                      {intl.formatMessage(
                        { id: 'FIXED.COMMISSION' },
                        {
                          value: taxonomy?.projectSingular,
                        },
                      )}
                    </label>
                    <div className='row'>
                      <div className='col-md-6 form-group'>
                        <InputGroup className='input-group-wrapper'>
                          <InputGroup.Text
                            className='bg-secondary-light text-black font-size-sm'
                            id='percentageCampaignCommission'
                          >
                            {intl.formatMessage({
                              id: 'PERCENTAGE',
                            })}
                          </InputGroup.Text>

                          <Field
                            name='percentageCampaignCommission'
                            component={Input}
                            className='rounded-start-0 text-black'
                            withFeedbackLabel={false}
                          />
                          <ErrorMessage
                            name='percentageCampaignCommission'
                            component='div'
                            className='invalid-feedback'
                          />
                        </InputGroup>
                      </div>
                      <div className='col-md-6 form-group'>
                        <InputGroup className='input-group-wrapper'>
                          <InputGroup.Text
                            className='bg-secondary-light text-black font-size-sm'
                            id='fixedCampaignCommission'
                          >{`${intl.formatMessage({
                            id: 'FIXED',
                          })} (${contractDetails.currencySymbol})`}</InputGroup.Text>

                          <Field
                            name='fixedCampaignCommission'
                            component={Input}
                            className='rounded-start-0 text-black'
                            withFeedbackLabel={false}
                          />
                          <ErrorMessage
                            name='fixedCampaignCommission'
                            component='div'
                            className='invalid-feedback'
                          />
                        </InputGroup>
                      </div>
                    </div>
                  </div>

                  <div className='form-group'>
                    <label className='text-black font-size-sm'>
                      Gateway Fees
                    </label>
                    <div className='row'>
                      <div className='col-md-6 form-group'>
                        <InputGroup className='input-group-wrapper'>
                          <InputGroup.Text
                            className='bg-secondary-light text-black font-size-sm'
                            id='percentageInvestmentFees'
                          >
                            {intl.formatMessage({
                              id: 'PERCENTAGE',
                            })}
                          </InputGroup.Text>

                          <Field
                            name='percentageGatewayFees'
                            component={Input}
                            className='rounded-start-0 text-black'
                            withFeedbackLabel={false}
                          />
                          <ErrorMessage
                            name='percentageGatewayFees'
                            component='div'
                            className='invalid-feedback'
                          />
                        </InputGroup>
                      </div>
                      <div className='col-md-6 form-group'>
                        <InputGroup className='input-group-wrapper'>
                          <InputGroup.Text
                            className='bg-secondary-light text-black font-size-sm'
                            id='fixedInvestmentFees'
                          >{`${intl.formatMessage({
                            id: 'FIXED',
                          })} (${contractDetails.currencySymbol})`}</InputGroup.Text>

                          <Field
                            name='fixedGatewayFees'
                            component={Input}
                            className='rounded-start-0 text-black'
                            withFeedbackLabel={false}
                          />
                          <ErrorMessage
                            name='fixedGatewayFees'
                            component='div'
                            className='invalid-feedback'
                          />
                        </InputGroup>
                      </div>
                    </div>
                  </div>
                  {currentPermissions?.update_revenue_fees && (
                    <button
                      className='btn btn-secondary px-10 text-white py-1 mt-5'
                      type='button'
                      disabled={[
                        isReconnecting,
                        isConnecting,
                        isInteracting,
                      ].includes(true)}
                      onClick={() => {
                        handleSubmitForm(values);
                      }}
                    >
                      {[isReconnecting, isConnecting, isInteracting].includes(
                        true,
                      ) ? (
                        <span
                          className='spinner-border spinner-border-sm'
                          role='status'
                          aria-hidden='true'
                        ></span>
                      ) : (
                        'Submit'
                      )}
                    </button>
                  )}
                </Form>
              );
            }}
          </Formik>
        </Col>
      </Row>
    </CardBody>
  );
};
